import { useCallback, useImperativeHandle, useRef } from 'react';
import { DefaultValues, FieldValues, UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { ThunkResult } from '@/shared/actions/thunk-result';
import { ISubmitFormResult, TFormValidation } from '@/shared/models/formSubmit';
import { useForm } from '@/shared/view/Form';

import { IRelatedInfoHandle } from './config';

interface IParams<TFieldValues extends FieldValues> {
  defaultValues: DefaultValues<TFieldValues>;
  onSubmit: (formValues: TFieldValues) => Promise<ThunkResult<ISubmitFormResult<TFieldValues>>>;
}

const useRelatedInfoForm = <TFieldValues extends FieldValues>(
  ref: React.ForwardedRef<IRelatedInfoHandle>,
  { defaultValues, onSubmit }: IParams<TFieldValues>,
): UseFormReturn<TFieldValues> => {
  const intl = useIntl();
  const isSucceedRef = useRef(false);

  const postForm = useCallback(
    async (formValues: TFieldValues): Promise<ISubmitFormResult<TFieldValues>> => {
      let isSucceed: boolean | undefined;
      let result: ISubmitFormResult<TFieldValues> = {};

      try {
        ({ isSucceed, result = {} } = await onSubmit(formValues));
      } catch {
        isSucceed = false;
      }

      const baseErrors = result.baseErrors?.filter(Boolean) ?? [];
      const hasValidationErrors = Boolean(
        result.validationErrors && Object.values(result.validationErrors).some(Boolean),
      );

      isSucceedRef.current = Boolean(isSucceed) && !hasValidationErrors && baseErrors.length === 0;

      if (isSucceedRef.current) {
        return {};
      }

      const fieldNames = Object.keys(formValues);
      const visibleValidationErrors = Object.fromEntries(
        Object.entries(result.validationErrors ?? {}).filter(
          ([name, errors]) => fieldNames.includes(name) && errors && errors.length > 0,
        ),
      ) as TFormValidation<TFieldValues>;

      if (Object.keys(visibleValidationErrors).length > 0) {
        return { validationErrors: visibleValidationErrors };
      }

      if (baseErrors.length > 0) {
        return { baseErrors };
      }

      return {
        baseErrors: [
          intl.formatMessage({
            defaultMessage: 'Something went wrong. please try again later.',
            id: 'yCM2I2',
            description: '500 Error page description',
          }),
        ],
      };
    },
    [onSubmit, intl],
  );

  const { handleSubmit, form } = useForm<TFieldValues>({
    onSubmit: postForm,
    defaultValues,
  });

  const { isDirty } = form.formState;

  useImperativeHandle(
    ref,
    () => ({
      submit: async () => {
        if (!isDirty) {
          return true;
        }

        isSucceedRef.current = false;
        await handleSubmit();

        return isSucceedRef.current;
      },
    }),
    [handleSubmit, isDirty],
  );

  return form;
};

export { useRelatedInfoForm };
